import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  notification_type: 'ai_generation' | 'post_published' | 'achievement' | 'system' | 'mention' | 'like' | 'comment';
  title: string;
  message: string;
  action_url?: string;
  thumbnail_url?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  read: boolean;
  created_at: string;
}

type NotificationCallback = (notification: Notification) => void;

let notificationChannel: RealtimeChannel | null = null;

export const subscribeToNotifications = (userId: string, callback: NotificationCallback) => {
  if (notificationChannel) {
    supabase.removeChannel(notificationChannel);
  }

  notificationChannel = supabase
    .channel(`notifications:${userId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'notifications',
      filter: `user_id=eq.${userId}`
    }, (payload) => {
      const notification = payload.new as Notification;
      callback(notification);
      showBrowserNotification(notification);
    })
    .subscribe();

  return () => {
    if (notificationChannel) {
      supabase.removeChannel(notificationChannel);
      notificationChannel = null;
    }
  };
};

export const showBrowserNotification = (notification: Notification) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const options: NotificationOptions = {
      body: notification.message,
      icon: notification.thumbnail_url || '/icon-192.png',
      badge: '/icon-192.png',
      tag: notification.id,
      requireInteraction: notification.priority === 'urgent',
      data: {
        url: notification.action_url
      }
    };

    const browserNotification = new Notification(notification.title, options);
    
    browserNotification.onclick = () => {
      window.focus();
      if (notification.action_url) {
        window.location.href = notification.action_url;
      }
      browserNotification.close();
    };
  }
};

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    return permission;
  }
  return 'denied';
};

export const hasNotificationPermission = (): boolean => {
  return 'Notification' in window && Notification.permission === 'granted';
};
